import { Mail, MapPin, Clock } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionWrap,
  FadeUp,
} from "@/components/sections/Primitives";
import ContactForm from "@/components/forms/ContactForm";

export default function ContactPage() {
  useSeo({
    title: "Contact Pizza Excellente",
    description:
      "Contact Pizza Excellente for franchise enquiries, partnerships, supplier opportunities, launch updates, and general brand information.",
    path: "/contact",
    ogImage: ASSETS.storefront,
    ogImageAlt: "Contact Pizza Excellente",
  });

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-48 pb-12 md:pb-20 bg-pe-mauve/40 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <SectionEyebrow>Contact</SectionEyebrow>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-ink mt-6 leading-[1.02] max-w-3xl">
            Let’s <span className="italic text-pe-maroon">connect.</span>
          </h1>
          <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed max-w-2xl">
            For franchise enquiries, partnerships, launch updates, or general information — please contact Pizza Excellente.
          </p>
        </div>
      </section>

      {/* Main content — info + form */}
      <SectionWrap testId="contact-main" tone="cream" className="!pt-12 md:!pt-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Info column */}
          <FadeUp className="lg:col-span-5 space-y-10">
            <div>
              <SectionEyebrow>General contact</SectionEyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-pe-ink mt-4 leading-tight">
                Building a neighborhood-first pizza brand for India.
              </h2>
              <p className="mt-5 text-pe-ink/75 leading-relaxed">
                To connect with the team, please email us using the address below or send a message through the form.
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-4 border-t border-pe-ink/10 pt-5">
                <Mail size={18} className="text-pe-maroon mt-1 shrink-0" />
                <div>
                  <div className="eyebrow text-pe-ink/60">Email</div>
                  <a
                    href={BRAND.mailto}
                    data-testid="contact-info-email"
                    className="text-pe-ink hover:text-pe-maroon text-lg font-serif"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-pe-ink/10 pt-5">
                <MapPin size={18} className="text-pe-maroon mt-1 shrink-0" />
                <div>
                  <div className="eyebrow text-pe-ink/60">Launching</div>
                  <p className="text-pe-ink text-lg font-serif">Greater Noida, Uttar Pradesh — Tier 2 & 3 India expansion.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-pe-ink/10 pt-5">
                <Clock size={18} className="text-pe-maroon mt-1 shrink-0" />
                <div>
                  <div className="eyebrow text-pe-ink/60">Response time</div>
                  <p className="text-pe-ink text-lg font-serif">We review every serious enquiry carefully.</p>
                </div>
              </div>
            </div>

            <div className="bg-pe-ink p-6 text-pe-cream">
              <div className="eyebrow text-pe-orange mb-3">Franchise enquiries</div>
              <p className="text-pe-cream/85 text-sm leading-relaxed">
                Please email <span className="text-pe-orange">{BRAND.email}</span> with your name, city, background, and level of interest —
                or use the form with “Franchise Enquiry” selected.
              </p>
            </div>
          </FadeUp>

          {/* Form column */}
          <FadeUp delay={0.1} className="lg:col-span-7">
            <div className="bg-white border border-pe-border p-6 md:p-10 lg:p-12">
              <div className="mb-8">
                <SectionEyebrow>Send us a message</SectionEyebrow>
                <h3 className="font-serif text-3xl md:text-4xl text-pe-ink mt-3 leading-tight">
                  Please share a few details below.
                </h3>
                <p className="mt-3 text-pe-ink/65">The team will review your enquiry and respond as appropriate.</p>
              </div>
              <ContactForm sourcePage="contact" testIdPrefix="contact-form" />
            </div>
          </FadeUp>
        </div>
      </SectionWrap>
    </div>
  );
}
